import { useTranslation } from 'react-i18next';
import { isPlannerEnabled, ProcessAnalyzerAppConfigSchema } from '@bliks-io/contracts';

import { FormFieldSwitch } from '@/components/form/FormFieldSwitch';
import { useSetTenantAppConfig } from '@/pages/admin/api/queries/useTenantApps';
import type { MergedAppRow } from '@/lib/merge-app-catalog';

interface TenantProcessAnalyzerConfigCellProps {
  tenantId: string;
  app: MergedAppRow;
}

/**
 * The per-tenant config editor for the BLIKS IO Process Assessment app, typed to that app's
 * config schema. Every configurable app gets its own cell rather than a
 * schema-driven renderer: the platform's config surface is generic, but each
 * app's shape is its own, and a generated form would be unreadable for a shape
 * nobody else has.
 *
 * The stored value is parsed rather than read: a config written before a later
 * schema change is unreadable here. Such a value reads as "planner off" — which
 * is what the worker makes of it too, through the very same helper — and stays
 * writable: the endpoint is a full replace, so the next save heals it. The
 * warning exists because an unreadable config is otherwise indistinguishable
 * from an unconfigured tenant.
 */
export function TenantProcessAnalyzerConfigCell({
  tenantId,
  app
}: TenantProcessAnalyzerConfigCellProps) {
  const { t } = useTranslation();
  const setConfig = useSetTenantAppConfig(tenantId);

  const unreadable =
    app.config != null && !ProcessAnalyzerAppConfigSchema.safeParse(app.config).success;

  return (
    <FormFieldSwitch
      id={`app-config-planner-enabled-${app.appId}`}
      label={t('adminSettings.tenant.appConfigPlannerEnabled')}
      error={unreadable ? t('adminSettings.tenant.appConfigUnreadable') : undefined}
      checked={isPlannerEnabled(app.config)}
      disabled={setConfig.isPending}
      // Full-replace semantics: the endpoint stores what it is given, so the
      // complete config object goes over the wire, not a patch.
      onCheckedChange={(plannerEnabled) =>
        setConfig.mutate({ appId: app.appId, config: { plannerEnabled } })
      }
    />
  );
}
